/** The road network: a cobblestone spanning tree over the towns, gravel spurs
 * to what is worth walking to twice, and the prune that cuts the result back
 * to the smallest network that still joins everything it was laid to join.
 *
 * Roads are laid BEFORE objects and spurred AFTER them - see the pass order in
 * `generate.ts` - which is why `layTrunkRoads` takes cells and `laySpurRoads`
 * takes the `GenState`.
 */

import type { AdventureMapState } from "@game/core";
import { RIVER_KINDS, ROAD_KINDS, setRoad } from "@game/core";
import type { Grid } from "./grid.js";
import {
  allNeighbours, cheapestPath8, chebyshev, drawnNeighbours, idx, inside, walkable, xOf, yOf,
} from "./grid.js";
import { NOISE_ONE, relief } from "./noise.js";
import type { GenState } from "./state.js";

/**
 * Roads are routed on EIGHT neighbours, and drawn that way.
 *
 * Every road overlay has a `diag.png` beside its cardinal atlas with a frame
 * for a band from the tile centre to a corner, so two road cells that share
 * only a corner are a road - `connectionMask` in the renderer reports the
 * corner as an arm whenever neither flanking cell is paved. That is what makes
 * a diagonal leg one 45-degree ribbon instead of a staircase of elbows, and it
 * is why `cheapestPath8` prices a diagonal step at root-two of a straight one
 * and charges for every change of heading: the cheapest route between two
 * points on open ground is a diagonal leg and a straight leg with one gentle
 * bend between them, the shape of a classic road.
 *
 * The cost field is what keeps that from being a ruler. A Dijkstra over a flat
 * field returns the straightest octile route, and a network of those reads as
 * a circuit board. The old fix added an independent random number to every
 * cell, which cannot work: per-cell noise only picks a different staircase.
 * `relief` is a field smooth over `LONG_PERIOD` cells instead, thresholded
 * into hills the road pays to cross, so the cheapest route bows away from the
 * straight line over tens of cells - the way a road that was walked into
 * existence goes round a hill rather than through it.
 */
const ROAD_STEP = 10;
/**
 * What a road pays to cross high ground, and how much of the map is high.
 *
 * A threshold, not a gradient, and the difference is the whole fix. A smooth
 * cost field cannot bend a route: a detour costs two extra cells up front while
 * a smoother lane only saves an average, so measured on a 60-cell route the
 * cheapest path moved off the straight line by zero cells at every amplitude
 * tried. A hill the route must not cross does bend it, because the penalty is
 * paid per cell for as long as the route stays on the hill. `ROAD_HIGH_GROUND`
 * of 540 out of `NOISE_ONE` leaves a little under half the map high, in blobs
 * `LONG_PERIOD` cells across, which is what the road then has to thread.
 *
 * `ROAD_HILL` is priced so that going round is cheaper than going over for a
 * hill of about that width and no wider: at 34 against a step of 10, crossing
 * twelve cells of hill costs 408 more than the flat, and the detour round it is
 * about twelve extra cells at 10. Larger and roads take absurd tours; smaller
 * and they cut straight over.
 */
const ROAD_HILL = 22;
const ROAD_HIGH_GROUND = Math.trunc((NOISE_ONE * 54) / 100);
const ROAD_THICKEN = 60;
const ROAD_SALT = 0x1a3b5c7d;
/**
 * What a road pays to cross a river cell, and the price of a lava flow.
 *
 * A road over a river is drawn as the road on top of the water, which is what
 * The classic draws too (the HD-edition grass shot has a cobble road crossing the
 * same river twice with nothing marking it) - but a road that runs ALONG a
 * river paints the water out for its whole length, so a river cell costs
 * about four steps: the router crosses where it has to, in one cell, and
 * never follows the bank. A lava flow is not crossed at all unless nothing
 * else connects the two towns (`bendPath` retries at `ROAD_LAVA` only when
 * the first search fails).
 */
const ROAD_FORD = 45;
const ROAD_LAVA = 400;

/**
 * Would paving `i` make a two-by-two square of road?
 *
 * That square is the "one-cell closed loop enclosing nothing" a critic picks
 * out of a screenshot. It does not look like a plaza, because the overlay tiles
 * are keyed on the cardinal mask only: four tiles each drawing a four-way
 * junction leave their outer corners bare, so the block renders as a little
 * ring of paving around a hole of grass. `pruneRoads` opens most of them
 * afterwards, but it cannot open one whose every cell carries a branch of its
 * own - so the cheap fix is to price it out while the path is still being
 * chosen, and leave the prune for what gets through.
 */
function wouldThicken(m: AdventureMapState, g: Grid, i: number): boolean {
  const x = xOf(g, i), y = yOf(g, i);
  for (let dy = -1; dy <= 0; dy++) {
    for (let dx = -1; dx <= 0; dx++) {
      const a = x + dx, b = y + dy;
      if (a < 0 || b < 0 || a + 1 >= g.width || b + 1 >= g.height) continue;
      let paved = 0;
      for (let q = 0; q < 4; q++) {
        const c = idx(g, a + (q & 1), b + (q >> 1));
        if (c === i || m.road[c] !== 0) paved++;
      }
      if (paved === 4) return true;
    }
  }
  // The eight-connected version of the same ring: the four cells around a
  // bare cell, each joined to the next by a corner. A spur that reaches the
  // network at a cell diagonal to TWO road cells closes one, and `pruneRoads`
  // cannot open it when every cell of the diamond carries a branch (seed 999
  // at 48: the neutral town's doorstep, the trunk both ways and the spur).
  for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]] as const) {
    const cx = x + dx, cy = y + dy;
    if (!inside(g, cx, cy) || m.road[idx(g, cx, cy)] !== 0) continue;
    let ring = 0;
    for (const [ex, ey] of [[0, -1], [1, 0], [0, 1], [-1, 0]] as const) {
      const rx = cx + ex, ry = cy + ey;
      if (rx === x && ry === y) continue;
      if (inside(g, rx, ry) && m.road[idx(g, rx, ry)] !== 0) ring++;
    }
    if (ring === 3) return true;
  }
  return false;
}

const roadCost = (m: AdventureMapState, g: Grid, i: number, lava = -1): number => {
  if (!walkable(m, i)) return -1;
  if (m.road[i] !== 0) return 2;
  let wet = 0;
  if (m.river[i] !== 0) {
    if (RIVER_KINDS[m.river[i]!] === "lava") { if (lava < 0) return -1; wet = lava; }
    else wet = ROAD_FORD;
  }
  const high = relief(xOf(g, i), yOf(g, i), ROAD_SALT) > ROAD_HIGH_GROUND;
  return ROAD_STEP + wet + (high ? ROAD_HILL : 0) + (wouldThicken(m, g, i) ? ROAD_THICKEN : 0);
};

/**
 * The route a road actually takes: the cheapest eight-connected path under the
 * hill field, with the router's own turn pricing deciding between the
 * equally-priced ways round a hill - the one with the fewest, gentlest bends.
 *
 * This used to be two passes, the second pulled onto a smoothed copy of the
 * first so that a diagonal leg came out as a staircase and not an L. The
 * router now steps diagonally, so the staircase and the L are both simply
 * dearer than the diagonal, and the guide has nothing left to decide.
 */
function bendPath(
  m: AdventureMapState, g: Grid, from: number, to: number,
): number[] | null {
  return cheapestPath8(g, from, to, (i) => roadCost(m, g, i))
    ?? cheapestPath8(g, from, to, (i) => roadCost(m, g, i, ROAD_LAVA));
}

function layRoad(
  m: AdventureMapState, g: Grid, path: readonly number[], kind: "cobble" | "gravel" | "dirt",
): void {
  for (const i of path) {
    // A junction keeps the better surface: a spur never downgrades the trunk.
    if (m.road[i] !== 0 && kind !== "cobble") continue;
    setRoad(m, xOf(g, i), yOf(g, i), 0, kind);
  }
}

/**
 * Minimum spanning tree over the town entrances, laid in cobblestone.
 *
 * One edge is priced differently: capital to capital. Cobblestone costs a hero
 * 50 movement points a step against grass's 100, so a direct trunk road between
 * two starting towns is a motorway that halves the distance the whole start
 * layout was arranged to create - 26 steps of open ground is a day and a half's
 * march, and 26 steps of cobblestone is most of one day. `CAPITAL_EDGE_PENALTY`
 * is larger than any real distance on a 252-cell map, so the tree uses such an
 * edge only when it is the only way to connect the network, and otherwise
 * routes the two capitals together through a neutral town - which is a road
 * with a garrison sitting on it, and that is the road the classic builds.
 */
const CAPITAL_EDGE_PENALTY = 100000;

export function layTrunkRoads(
  m: AdventureMapState, g: Grid, towns: readonly number[],
  capital: readonly boolean[],
): void {
  if (towns.length < 2) return;
  const parent = towns.map((_, i) => i);
  const find = (i: number): number => {
    let x = i;
    while (parent[x] !== x) { parent[x] = parent[parent[x]!]!; x = parent[x]!; }
    return x;
  };
  const edges: { a: number; b: number; d: number }[] = [];
  for (let a = 0; a < towns.length; a++) {
    for (let b = a + 1; b < towns.length; b++) {
      const both = (capital[a] ?? false) && (capital[b] ?? false);
      edges.push({
        a, b,
        d: chebyshev(g, towns[a]!, towns[b]!) + (both ? CAPITAL_EDGE_PENALTY : 0),
      });
    }
  }
  edges.sort((p, q) => p.d - q.d || p.a - q.a || p.b - q.b);
  for (const e of edges) {
    const ra = find(e.a), rb = find(e.b);
    if (ra === rb) continue;
    const path = bendPath(m, g, towns[e.a]!, towns[e.b]!);
    if (path === null) continue;
    parent[ra] = rb;
    layRoad(m, g, path, "cobble");
  }
}

const ROADWORTHY: ReadonlySet<string> = new Set(["mine", "dwelling", "garrison"]);

/**
 * Join the things a player walks to twice onto the trunk - but only those, and
 * only when they are genuinely off it. Spurring everything produces a grid, and
 * a grid is the other way a generated road network announces itself.
 */
export function laySpurRoads(s: GenState): number[] {
  const { map: m, g } = s;
  const served: number[] = [];
  const roadCells: number[] = [];
  for (let i = 0; i < m.road.length; i++) if (m.road[i] !== 0) roadCells.push(i);
  if (roadCells.length === 0) return served;

  for (let k = 0; k < s.objects.length; k++) {
    const o = s.objects[k]!;
    if (!ROADWORTHY.has(o.kind)) continue;
    const entrance = s.placements[k]?.entrances[0];
    if (entrance === undefined) continue;
    let nearest = -1, nearestD = 1 << 30;
    for (const c of roadCells) {
      const d = chebyshev(g, c, entrance);
      if (d < nearestD) { nearestD = d; nearest = c; }
    }
    // Already on the network, or too far for a spur to be believable.
    if (nearest < 0 || nearestD <= 2 || nearestD > 10) continue;
    const path = bendPath(m, g, entrance, nearest);
    if (path === null) continue;
    layRoad(m, g, path, "gravel");
    served.push(entrance);
    for (const i of path) roadCells.push(i);
  }
  return served;
}

/**
 * Cut the road network back to the smallest one that still joins everything it
 * was laid to join.
 *
 * Two artefacts fall out of laying a network as a union of shortest paths, and
 * both of them are how a generated map announces itself. A path that rejoins an
 * existing road at a shallow angle closes a **one-cell loop** - a ring of four
 * cells around a single square of grass, which no cartwright ever built. And a
 * path that was laid to an object which a later pass moved, or a spur whose
 * last cells were shared with a trunk laid afterwards, leaves a **dead-end stub
 * in open grass**, a road that goes nowhere.
 *
 * `anchors` is the list of cells the network exists to reach: town entrances,
 * and the object entrances `laySpurRoads` actually spurred. Everything else is
 * only worth keeping if some anchor needs it. So the pass removes any cell it
 * can remove without changing which anchors can reach which - leaf first, which
 * is what deletes a stub all the way back to its junction, then any remaining
 * cell, which is what opens a loop.
 *
 * It preserves the *partition*, not "all anchors connected": a map can legally
 * have two road components (an island's spur, a zone the trunk never reached),
 * and turning that into a hard requirement would either fail or bulldoze.
 */
export function pruneRoads(
  m: AdventureMapState, g: Grid, anchors: readonly number[],
): { stubs: number; loops: number } {
  const n = g.width * g.height;
  const isRoad = (i: number): boolean => i >= 0 && i < n && m.road[i] !== 0;
  // `served` is the fixed question the pass has to keep answering the same way;
  // `keep` is the moving guard that stops the leaf prune eating a terminus, and
  // the loop pass is allowed to move it. They start equal and then diverge.
  const served = [...new Set(anchors.filter((a) => isRoad(a)))].sort((a, b) => a - b);
  const keep = new Set<number>(served);

  const buf: number[] = [];
  // Over the DRAWN graph: a corner neighbour counts only when the join would
  // be drawn as a diagonal band, so a cell the leaf prune calls a dead end is
  // one the player sees as a dead end.
  const degree = (i: number): number => drawnNeighbours(g, isRoad, i, buf).length;
  const drop = (i: number): void => { setRoad(m, xOf(g, i), yOf(g, i), 0, "none"); };

  // Pruning only ever removes cells, so the road plane at entry is a superset
  // of every later state and every scan below can walk this list instead of the
  // whole grid. That is the difference between a millisecond and a second on a
  // 252-cell map, where the loop check runs once per road cell.
  const laid: number[] = [];
  for (let i = 0; i < n; i++) if (isRoad(i)) laid.push(i);

  /** Component id per anchor, over the road plane as it stands. */
  const comp = new Int32Array(n);
  const signature = (): string => {
    comp.fill(-1);
    let next = 0;
    for (const i of laid) {
      if (!isRoad(i) || comp[i] !== -1) continue;
      const id = next++;
      const stack = [i];
      comp[i] = id;
      while (stack.length > 0) {
        const cur = stack.pop()!;
        for (const nb of drawnNeighbours(g, isRoad, cur, buf)) {
          if (comp[nb] !== -1) continue;
          comp[nb] = id;
          stack.push(nb);
        }
      }
    }
    // What an anchor actually needs is a road *at its door*, not a road cell
    // on top of it, so an anchor that is no longer paved still counts as long
    // as it touches the network. That is what lets the loop pass below break a
    // ring one of whose four cells happens to be a mine's doorstep.
    const at = (a: number): number => {
      if (isRoad(a)) return comp[a]!;
      for (const nb of allNeighbours(g, a, buf)) if (isRoad(nb)) return comp[nb]!;
      return -1;
    };
    // Anchors in ascending cell order, each written as the *first* anchor of
    // its component: a stable name for the partition that does not move when
    // an unrelated component's id shifts.
    const firstOf = new Map<number, number>();
    for (const a of served) {
      const c = at(a);
      if (!firstOf.has(c)) firstOf.set(c, a);
    }
    return served.map((a) => String(firstOf.get(at(a)))).join(",");
  };

  let stubs = 0, loops = 0;

  const leafPrune = (): void => {
    for (let pass = 0; pass < laid.length + 1; pass++) {
      let cut = 0;
      for (const i of laid) {
        if (!isRoad(i) || keep.has(i)) continue;
        if (degree(i) <= 1) { drop(i); cut++; stubs++; }
      }
      if (cut === 0) return;
    }
  };

  leafPrune();
  const want = signature();
  for (const i of laid) {
    if (!isRoad(i)) continue;
    // Only a cell with two or more road neighbours can be closing a loop; a
    // leaf is already gone and an isolated cell cannot matter.
    if (degree(i) < 2) continue;
    const wasAnchor = keep.has(i);
    const was = m.road[i]!;
    drop(i);
    if (signature() === want) {
      loops++;
      if (wasAnchor) {
        // The doorstep moves one cell: the road stops at the entrance instead
        // of running across it, and the cell it stops on is what the leaf prune
        // must now protect.
        keep.delete(i);
        for (const nb of allNeighbours(g, i, buf)) if (isRoad(nb)) { keep.add(nb); break; }
      }
      leafPrune();
    } else {
      setRoad(m, xOf(g, i), yOf(g, i), 0, ROAD_KINDS[was] ?? "dirt");
    }
  }
  leafPrune();
  return { stubs, loops };
}

/**
 * Pave the middle of every road DIAMOND: a bare cell whose four axis
 * neighbours are all road, each joined to the next by a corner.
 *
 * It is the eight-connected cousin of the two-by-two square `wouldThicken`
 * prices out, and it is the one ring `pruneRoads` cannot open: a doorstep
 * whose trunk leaves it diagonally both ways, plus a spur arriving at the
 * cell below, makes a diamond in which every cell carries a branch (seed 999
 * at 48). Drawn, it is a ring of four corner bands around a hole of grass.
 * Paving the hole turns it into a plus-junction - the corner joins are no
 * longer drawn once their flanks are paved - which is what the junction was.
 */
export function fillDiamonds(m: AdventureMapState, g: Grid): number {
  let filled = 0;
  for (let y = 1; y + 1 < g.height; y++) {
    for (let x = 1; x + 1 < g.width; x++) {
      const i = idx(g, x, y);
      if (m.road[i] !== 0 || !walkable(m, i) || m.occupant[i] !== 0) continue;
      let best = 0;
      let ring = 0;
      for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]] as const) {
        const k = m.road[idx(g, x + dx, y + dy)]!;
        if (k === 0) break;
        ring++;
        if (k > best) best = k;
      }
      if (ring < 4) continue;
      setRoad(m, x, y, 0, ROAD_KINDS[best] ?? "dirt");
      filled++;
    }
  }
  return filled;
}
